"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRememberCalendarFilters = exports.CALENDAR_FILTERS_TABLE_NAME = void 0;
const react_1 = __importDefault(require("react"));
const set_calendar_filters_1 = __importDefault(require("@app/ac/set-calendar-filters"));
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const useRememberTableConfig_1 = require("@app/util/hooks/useRememberTableConfig");
const use_workspace_table_config_1 = require("@app/util/use-workspace-table-config");
exports.CALENDAR_FILTERS_TABLE_NAME = 'schedule-calendar-filters';
// Deep-clone the filters whenever they cross between 'calendarFilters' and 'tablesConfig'.
// Without this the two store slices would hold the SAME object references, and safeStringify's
// circular replacer (app/util/misc.js) - which dedupes any reference it has already seen, not just
// true cycles - serializes whichever copy comes second as an array of nulls when persisting to
// localStorage. An independent object graph lets both copies survive persistence intact.
const cloneFilters = (filters) => JSON.parse(JSON.stringify(filters));
/**
 * Persists 'store.calendarFilters' per workspace.
 *
 * 'setWorkspaceId' resets the filters to [] on every workspace switch (see
 * app/ac/set-workspace-id.js). This hook restores the newly selected workspace's last filters
 * and mirrors every later user change back into 'tablesConfig', so they survive both workspace
 * switches and page reloads.
 *
 * Must be called from a component that stays mounted with the calendar - the Filters panel itself
 * is unmounted whenever it is closed, so it cannot do the restoring.
 */
const useRememberCalendarFilters = () => {
    const dispatch = (0, store_1.useRootDispatch)();
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const calendarFilters = (0, store_1.useRootSelector)((state) => state.calendarFilters);
    const storedConfig = (0, use_workspace_table_config_1.useWorkspaceTableConfig)(exports.CALENDAR_FILTERS_TABLE_NAME);
    const rememberTableConfig = (0, useRememberTableConfig_1.useRememberTableConfig)(exports.CALENDAR_FILTERS_TABLE_NAME);
    // the filters we have already synced, together with the workspace they belong to. Lets us tell
    // an actual workspace switch apart from a user editing the current workspace's filters, and
    // stops us from re-saving a value we just restored ourselves.
    const synced = react_1.default.useRef(null);
    react_1.default.useEffect(() => {
        if (!(0, util_1.isValue)(workspaceId))
            return;
        // workspace just changed (or first mount) -> load this workspace's filters. We never save on
        // this path: the value currently in the store belongs to the previous workspace (or is the []
        // that setWorkspaceId just reset it to), so saving it would clobber this workspace's config.
        if (synced.current?.workspaceId !== workspaceId) {
            const stored = storedConfig?.filters;
            if ((0, util_1.isValue)(stored)) {
                const clean = stored.filter(util_1.isValue);
                synced.current = { workspaceId, filters: clean };
                if (util_1.miscUtil.safeStringify(clean) !== util_1.miscUtil.safeStringify(calendarFilters)) {
                    void dispatch((0, set_calendar_filters_1.default)(cloneFilters(clean)));
                }
            }
            else {
                // nothing stored for this workspace yet - adopt whatever is currently loaded (e.g. the
                // legacy directly-persisted value on the first reload after this feature shipped) and seed
                // the per-workspace store from it, instead of wiping it to []
                synced.current = { workspaceId, filters: calendarFilters };
                rememberTableConfig([['filters', cloneFilters(calendarFilters)]]);
            }
            return;
        }
        // same workspace, filters changed -> a real user edit, persist it
        if (util_1.miscUtil.safeStringify(synced.current.filters) ===
            util_1.miscUtil.safeStringify(calendarFilters))
            return;
        synced.current = { workspaceId, filters: calendarFilters };
        rememberTableConfig([['filters', cloneFilters(calendarFilters)]]);
        // 'storedConfig' and 'rememberTableConfig' are intentionally not dependencies: 'storedConfig'
        // changes on every save (which would re-trigger a restore in a loop) and 'rememberTableConfig'
        // is rebuilt on every render. We only react to an actual workspace switch or a filters change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId, calendarFilters]);
};
exports.useRememberCalendarFilters = useRememberCalendarFilters;
